/**
 * Ticket API Service
 *
 * REST client for /api/tickets — CRUD and CAB-driven generation.
 */

import api from '../utils/axios';
import type { Ticket, TicketWorkType, TicketStatus, TicketPriority, TicketSource, TicketExpertise, TicketPlatformType } from '../pages/TicketsPage';
import type { PageSize } from '../utils/pagination';

const BASE = '/tickets';

// ─── API shapes (snake_case from backend) ────────────────────────────────────

export interface ApiAssignTo {
  name: string;
  email: string;
}

export type TicketEvaluationStatus = 'pending' | 'completed' | 'failed';

/** A CAB item that holds what a ticket requires. */
export interface TicketCabLink {
  section: string;
  itemId: string | null;
  label: string;
}

/**
 * Whether the CAB holds what a Web ticket requires: "pending" = the ticket changed
 * since, "new" = it was never in the CAB. null = the project has no two-way sync.
 */
export type TicketSyncState = 'in_sync' | 'pending' | 'new';

/** What deleting Web tickets does to the CAB: "remove" proposes a CAB change for review. */
export type TicketCabAction = 'remove' | 'keep';

interface ApiTicketSummary {
  id: string;
  project_id: string;
  title: string;
  status: TicketStatus;
  priority: TicketPriority;
  source: TicketSource;
  work_type?: TicketWorkType;
  expertise: TicketExpertise;
  platform_type: TicketPlatformType;
  assign_to?: ApiAssignTo;
  due_date?: string;
  created_date: string;
  created_by: string;
  ai_evaluation_status?: TicketEvaluationStatus;
  estimated_effort?: number | string;
  epic_id?: string;
  epic?: string;
  build_wave?: number;
}

interface ApiTicket extends ApiTicketSummary {
  description: string;
  note?: string;
  ai_reasoning?: string;
  implementation_steps?: Array<{ step: string; human_required: boolean }>;
  cab_links?: Array<{ section: string; item_id: string | null; label: string }>;
  sync_state?: TicketSyncState | null;
}

export interface EvaluateResponse {
  job_id: string | null;
  ticket_ids: string[];
  message: string;
}

export interface EvaluationJobStatus {
  job_id: string;
  status: string;
  total: number;
  completed: number;
  results: Array<{
    ticket_id: string;
    status: string;
    data: {
      evaluation: string;
      reasoning: string;
      implementation_steps: Array<{ step: string; human_required: boolean }>;
    };
  }> | null;
}

// ─── Query / pagination types ─────────────────────────────────────────────────

export type SortField = 'id' | 'title' | 'status' | 'priority' | 'work_type' | 'source' | 'assign_to' | 'created_date';
export type SortDir = 'asc' | 'desc';
// Re-exported from the shared pagination utils so both TicketsPage and other
// list views can import PAGE_SIZE_OPTIONS / PageSize from a single place.
export { PAGE_SIZE_OPTIONS, type PageSize } from '../utils/pagination';

export interface TicketListParams {
  page: number;
  pageSize: PageSize;
  sortField: SortField;
  sortDir: SortDir;
  search?: string;
  status?: string[];
  priority?: string[];
  source?: string[];
  workType?: string[];
  expertise?: string[];
  view?: 'all' | 'my';
}

export interface TicketListResult {
  tickets: Ticket[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateTicketRequest {
  title: string;
  description?: string;
  note?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  source?: TicketSource;
  work_type?: TicketWorkType;
  expertise?: TicketExpertise;
  platform_type?: TicketPlatformType;
  assign_to?: ApiAssignTo;
  due_date?: string;
  epic_id?: string | null;
}

export interface UpdateTicketRequest {
  title?: string;
  description?: string;
  note?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  source?: TicketSource;
  work_type?: TicketWorkType;
  expertise?: TicketExpertise;
  platform_type?: TicketPlatformType;
  assign_to?: ApiAssignTo | null;
  due_date?: string | null;
  epic_id?: string | null;
  /**
   * Title / description as they were when editing started, for whichever of the two
   * is being changed. The save is refused with a 409 when the ticket no longer holds them.
   */
  expected?: { title?: string; description?: string };
}

/** A save refused because the ticket changed while it was being edited (HTTP 409). */
export interface TicketConflict {
  message: string;
  /** Fields that changed meanwhile, e.g. ["title"]. */
  conflicts: string[];
  /** The ticket as it is now. */
  ticket: Ticket;
}

export interface TranslateJobStartResult {
  job_id: string | null;
  total: number;
  message: string;
}

export interface TranslateJobStatus {
  /** null while the job is still being submitted to the external API. */
  job_id: string | null;
  total: number;
  /** Work units the external API has finished; advances once per backend poll. */
  completed: number;
  status: 'submitting' | 'pending';
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

function mapSummary(t: ApiTicketSummary): Ticket {
  return {
    id: t.id,
    projectId: t.project_id,
    title: t.title,
    description: '',
    status: t.status,
    priority: t.priority,
    source: t.source,
    workType: t.work_type,
    expertise: t.expertise,
    platformType: t.platform_type,
    assignTo: t.assign_to ?? null,
    dueDate: t.due_date,
    createdDate: t.created_date,
    createdBy: t.created_by,
    aiEvaluationStatus: t.ai_evaluation_status,
    estimatedEffort: t.estimated_effort,
    epicId: t.epic_id,
    epic: t.epic,
    buildWave: t.build_wave,
  };
}

function mapTicket(t: ApiTicket): Ticket {
  return {
    ...mapSummary(t),
    description: t.description,
    note: t.note,
    aiReasoning: t.ai_reasoning,
    implementationSteps: t.implementation_steps?.map(s => ({ step: s.step, humanRequired: s.human_required })),
    cabLinks: t.cab_links?.map(l => ({ section: l.section, itemId: l.item_id, label: l.label })),
    syncState: t.sync_state ?? null,
  };
}

/** The conflict behind a 409 from a ticket save; null for any other error. */
export function ticketConflictFromError(err: unknown): TicketConflict | null {
  const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  const detail = res?.status === 409 ? res.data?.detail : undefined;
  if (!detail || typeof detail !== 'object') return null;
  const { message, conflicts, ticket } = detail as { message?: unknown; conflicts?: unknown; ticket?: unknown };
  if (!ticket || typeof ticket !== 'object') return null;
  return {
    message: typeof message === 'string' ? message : '',
    conflicts: Array.isArray(conflicts) ? conflicts.filter((c): c is string => typeof c === 'string') : [],
    ticket: mapTicket(ticket as ApiTicket),
  };
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function listTickets(projectId: string, params: TicketListParams): Promise<TicketListResult> {
  const q = new URLSearchParams();
  q.set('page', String(params.page));
  q.set('page_size', String(params.pageSize));
  q.set('sort_field', params.sortField);
  q.set('sort_dir', params.sortDir);
  if (params.view) q.set('view', params.view);
  if (params.search) q.set('search', params.search);
  params.status?.forEach(v => q.append('status', v));
  params.priority?.forEach(v => q.append('priority', v));
  params.source?.forEach(v => q.append('source', v));
  params.workType?.forEach(v => q.append('work_type', v));
  params.expertise?.forEach(v => q.append('expertise', v));

  const res = await api.get<{
    tickets: ApiTicketSummary[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }>(`${BASE}/${projectId}?${q.toString()}`);

  return {
    tickets: res.data.tickets.map(mapSummary),
    total: res.data.total,
    page: res.data.page,
    pageSize: res.data.page_size,
    totalPages: res.data.total_pages,
  };
}

export async function listTicketIds(projectId: string, params: Omit<TicketListParams, 'page' | 'pageSize'>): Promise<string[]> {
  const q = new URLSearchParams();
  q.set('sort_field', params.sortField);
  q.set('sort_dir', params.sortDir);
  if (params.view) q.set('view', params.view);
  if (params.search) q.set('search', params.search);
  params.status?.forEach(v => q.append('status', v));
  params.priority?.forEach(v => q.append('priority', v));
  params.source?.forEach(v => q.append('source', v));
  params.workType?.forEach(v => q.append('work_type', v));
  params.expertise?.forEach(v => q.append('expertise', v));

  const res = await api.get<string[]>(`${BASE}/${projectId}/ids?${q.toString()}`);
  return res.data;
}

export async function getTicket(projectId: string, ticketId: string): Promise<Ticket> {
  const res = await api.get<ApiTicket>(`${BASE}/${projectId}/${ticketId}`);
  return mapTicket(res.data);
}

export async function createTicket(projectId: string, data: CreateTicketRequest): Promise<Ticket> {
  const res = await api.post<ApiTicket>(`${BASE}/${projectId}`, data);
  return mapTicket(res.data);
}

export async function updateTicket(projectId: string, ticketId: string, data: UpdateTicketRequest): Promise<Ticket> {
  const res = await api.patch<ApiTicket>(`${BASE}/${projectId}/${ticketId}`, data);
  return mapTicket(res.data);
}

/** `cabAction` is for Web projects only; leave it out elsewhere. */
export async function deleteTickets(
  projectId: string,
  ticketIds: string | string[],
  cabAction?: TicketCabAction,
): Promise<number> {
  const ids = Array.isArray(ticketIds) ? ticketIds : [ticketIds];
  const res = await api.delete<{ deleted: number }>(`${BASE}/${projectId}`, {
    data: cabAction ? { ticket_ids: ids, cab_action: cabAction } : { ticket_ids: ids },
  });
  return res.data.deleted;
}

export async function getCabTicketCount(projectId: string): Promise<number> {
  const res = await api.get<{ count: number }>(`${BASE}/${projectId}/cab-count`);
  return res.data.count;
}

export async function getTicketCounts(projectId: string): Promise<{ all: number; my: number }> {
  const res = await api.get<{ all: number; my: number }>(`${BASE}/${projectId}/counts`);
  return res.data;
}

// ─── Epics (build-order / waves) ───────────────────────────────────────────────

export interface Epic {
  id: string;
  projectId: string;
  name: string;
  order: number;        // 1-based wave number
  seq: number;          // stable per-epic number → EPIC-00X (survives reorder)
  ticketCount: number;
}

interface ApiEpic {
  id: string;
  project_id: string;
  name: string;
  order: number;
  seq: number;
  ticket_count: number;
}

function mapEpic(e: ApiEpic): Epic {
  return { id: e.id, projectId: e.project_id, name: e.name, order: e.order, seq: e.seq ?? e.order, ticketCount: e.ticket_count };
}

/** Format an epic's stable number as EPIC-00X. */
export function epicCode(seq: number): string {
  return `EPIC-${String(seq).padStart(3, '0')}`;
}

export async function listEpics(projectId: string): Promise<Epic[]> {
  const res = await api.get<{ epics: ApiEpic[] }>(`${BASE}/${projectId}/epics`);
  return res.data.epics.map(mapEpic);
}

export async function createEpic(projectId: string, name: string, position?: number): Promise<Epic> {
  const res = await api.post<ApiEpic>(`${BASE}/${projectId}/epics`, { name, position });
  return mapEpic(res.data);
}

export async function renameEpic(projectId: string, epicId: string, name: string): Promise<Epic> {
  const res = await api.patch<ApiEpic>(`${BASE}/${projectId}/epics/${epicId}`, { name });
  return mapEpic(res.data);
}

export async function reorderEpics(projectId: string, orderedEpicIds: string[]): Promise<Epic[]> {
  const res = await api.post<{ epics: ApiEpic[] }>(`${BASE}/${projectId}/epics/reorder`, { ordered_epic_ids: orderedEpicIds });
  return res.data.epics.map(mapEpic);
}

export async function deleteEpic(projectId: string, epicId: string): Promise<void> {
  await api.delete(`${BASE}/${projectId}/epics/${epicId}`);
}

export interface EpicGroup {
  epic: Epic | null;   // null = unassigned tickets
  done: number;
  tickets: Ticket[];
}

export async function listEpicsGrouped(projectId: string): Promise<EpicGroup[]> {
  const res = await api.get<{ groups: Array<{ epic: ApiEpic | null; done: number; tickets: ApiTicketSummary[] }> }>(
    `${BASE}/${projectId}/epics/grouped`,
  );
  return res.data.groups.map(g => ({
    epic: g.epic ? mapEpic(g.epic) : null,
    done: g.done,
    tickets: g.tickets.map(mapSummary),
  }));
}

/**
 * Set the build order of tickets WITHIN an epic from the full ordered id list.
 * Handles same-epic reordering and cross-epic move-with-position (a ticket
 * dragged in from another epic is re-assigned to `epicId` at its dropped slot).
 * Returns the refreshed grouped view so the caller can resync.
 */
export async function reorderEpicTickets(
  projectId: string,
  epicId: string,
  orderedTicketIds: string[],
): Promise<EpicGroup[]> {
  const res = await api.post<{ groups: Array<{ epic: ApiEpic | null; done: number; tickets: ApiTicketSummary[] }> }>(
    `${BASE}/${projectId}/epics/${epicId}/tickets/reorder`,
    { ordered_ticket_ids: orderedTicketIds },
  );
  return res.data.groups.map(g => ({
    epic: g.epic ? mapEpic(g.epic) : null,
    done: g.done,
    tickets: g.tickets.map(mapSummary),
  }));
}

export type EpicGenStatus = 'idle' | 'in_progress' | 'completed' | 'failed';

export interface EpicStatus {
  status: EpicGenStatus;
  trigger?: 'cab' | 'manual';
  updatedAt?: string;
  epicCount?: number;
  totalTickets?: number;
  unsortedCount?: number;
  error?: string;
}

export async function getEpicStatus(projectId: string): Promise<EpicStatus> {
  const res = await api.get<{
    status: EpicGenStatus;
    trigger?: 'cab' | 'manual';
    updated_at?: string;
    epic_count?: number;
    total_tickets?: number;
    unsorted_count?: number;
    error?: string;
  }>(`${BASE}/${projectId}/epics/status`);
  return {
    status: res.data.status,
    trigger: res.data.trigger,
    updatedAt: res.data.updated_at,
    epicCount: res.data.epic_count,
    totalTickets: res.data.total_tickets,
    unsortedCount: res.data.unsorted_count,
    error: res.data.error,
  };
}

export async function generateEpics(projectId: string): Promise<{ epics: Epic[]; totalTickets: number; unsortedCount: number; message: string }> {
  const res = await api.post<{ epics: ApiEpic[]; total_tickets: number; unsorted_count: number; message: string }>(`${BASE}/${projectId}/epics/generate`, {});
  return {
    epics: res.data.epics.map(mapEpic),
    totalTickets: res.data.total_tickets,
    unsortedCount: res.data.unsorted_count,
    message: res.data.message,
  };
}

export async function generateTicketsFromCAB(projectId: string): Promise<TranslateJobStartResult> {
  const res = await api.post<TranslateJobStartResult>(
    `${BASE}/${projectId}/generate-from-cab`,
  );
  return res.data;
}

export async function getTranslateJobStatus(projectId: string): Promise<TranslateJobStatus | null> {
  const res = await api.get<TranslateJobStatus | null>(`${BASE}/${projectId}/translate/status`);
  return res.data;
}

export async function checkTranslateJob(projectId: string): Promise<{ status: string } & Record<string, any>> {
  const res = await api.get<{ status: string } & Record<string, any>>(`${BASE}/${projectId}/translate/check`);
  return res.data;
}

export async function evaluateTickets(projectId: string, ticketIds: string[]): Promise<EvaluateResponse> {
  const res = await api.post<EvaluateResponse>(`${BASE}/${projectId}/evaluate`, {
    ticket_ids: ticketIds,
  });
  return res.data;
}

export async function checkEvaluationJob(projectId: string, ticketId: string): Promise<EvaluationJobStatus> {
  const res = await api.get<EvaluationJobStatus>(`${BASE}/${projectId}/evaluate/check/${ticketId}`);
  return res.data;
}
